import React, { useState, useLayoutEffect, useEffect, useCallback } from 'react'
import { Stepper, Step, StepLabel, Alert, Stack, TextField, InputLabel } from '@mui/material'
import { Extra, ValidationButton, Footer, Icon } from './components'
import { grayText2, primary } from '@/constant'
import { UserVerification, IdentificationType } from '@/types'
import { useMutation } from '@tanstack/react-query'
import { userVerification } from '@/api'
import Form, { FormItem } from '@/components/Form'
import Text from '@/components/Text'
import Select from '@/components/Select'
import { Exclamation } from '@/icon'
import QRCode from './qr'
import { UserVerificationInfo } from '@/types'
export interface FormCardProps {
  onClose: () => void
  info?: UserVerificationInfo
}

const steps = ['填写基本信息', '证件完成']

const identityOptions = () => {
  const options = []
  for (const key in IdentificationType) {
    options.push({
      value: key,
      label: IdentificationType[key as keyof typeof IdentificationType],
    })
  }
  return options
}

const FormCard: React.FC<FormCardProps> = (props) => {
  const { onClose, info } = props
  const [step, setStep] = useState<number>(0)
  const [formValue, setFormValue] = useState<UserVerification>({
    cert_type: info?.cert_type ?? '',
    cert_no: '',
    cert_name: '',
  })
  const [hasError, setHasError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [validating, setValidation] = useState<boolean>(false)
  const [url, setUrl] = useState<string | null>()

  const verificationMutation = useMutation(userVerification, {
    onError: (error) => {
      setValidation(false)
      setLoading(false)
    },
    onSuccess: (data) => {
      setUrl(data.url)
      setValidation(false)
      setLoading(false)
    },
    onMutate: () => {
      setValidation(true)
      setLoading(true)
    },
  })

  const handleGetVerificationUrl = useCallback(() => {
    verificationMutation.mutate(formValue)
  }, [formValue, verificationMutation])

  const handleFinished = useCallback(() => {
    setStep(1)
    setUrl(null)
  }, [])

  const handleRemoveUrl = useCallback(() => {
    setUrl(null)
  }, [])

  const handleChangeFormValue = useCallback((value: UserVerification) => {
    setFormValue(value)
  }, [])

  const handleChangeValidate = useCallback((valdiateState: boolean) => {
    setHasError(valdiateState)
  }, [])

  return (
    <Stack sx={{ gap: '48px', flexBasis: '80%' }}>
      <Stepper activeStep={step}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': { fontSize: '20px' },
              }}
              StepIconProps={{ style: { fontSize: '32px' } }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      {step === 0 && (
        <>
          <Alert severity='info' sx={{ margin: '0 10%' }}>
            请填写您的真实身份信息，仅需 1～3 分钟即可完成认证。若您在认证过程中遇到任何问题，请联系官方客服。
          </Alert>
          <Form
            sx={{ margin: '0 10%' }}
            initialValue={formValue}
            // @ts-ignore
            onFormValueChange={handleChangeFormValue}
            // @ts-ignore
            onValidate={handleChangeValidate}
            validating={validating}
          >
            <FormItem
              label='cert_type'
              text='证件类型'
              rules={{
                required: true,
                validator: (value: string) => value?.length > 0,
              }}
            >
              <Select options={identityOptions()} />
            </FormItem>
            <FormItem
              label='cert_no'
              text='证件号码'
              rules={{
                required: true,
                // errorMessage: "密码不能为空且限长32",
                validator: (value: string) => value?.length > 0,
              }}
            >
              <TextField fullWidth={true} type='text' />
            </FormItem>
            <FormItem
              label='cert_name'
              text='真实姓名'
              rules={{
                required: true,
                // errorMessage: "密码不能为空且限长32",
                validator: (value: string) => value?.length > 0,
              }}
            >
              <TextField fullWidth={true} type='text' />
            </FormItem>
            <div>
              <Extra>
                <InputLabel>人脸认证</InputLabel>
                <Text
                  weight='regular'
                  sx={{
                    fontSize: '14px',
                    color: grayText2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Exclamation sx={{ color: primary }} />
                  请先完成以上必填选项，再点击生成
                </Text>
              </Extra>
            </div>
            <div style={{ marginTop: '24px' }}>
              <ValidationButton
                onClick={handleGetVerificationUrl}
                disabled={hasError || loading}
                sx={{ width: '200px', height: '40px' }}
              >
                <Text size='xs'>点击生成二维码</Text>
              </ValidationButton>
            </div>
          </Form>
        </>
      )}
      {step === 1 && <Finished onClose={onClose} />}
      {/*    @ts-ignore */}
      <QRCode url={url} onClose={handleRemoveUrl} onFinish={handleFinished} />
    </Stack>
  )
}

const Finished = ({ onClose }: { onClose: () => void }) => {
  const [second, setSecond] = useState<number>(3)

  useLayoutEffect(() => {
    const countDown = setInterval(() => {
      setSecond((second) => {
        if (second === 0) {
          return 0
        } else {
          return second - 1
        }
      })
    }, 1000)

    return () => {
      clearInterval(countDown)
    }
  }, [])

  useEffect(() => {
    if (second === 0) {
      onClose()
    }
  }, [onClose, second])

  return (
    <Footer sx={{ flexDirection: 'column', minHeight: '400px' }}>
      <Icon />
      <Text>已完成认证</Text>
      <Text size='xs' sx={{ color: grayText2 }}>
        {second}s后, 自动跳转
      </Text>
    </Footer>
  )
}

export default FormCard
